// Brand kit helpers (WS-2.5 dashboard surface). The generate endpoint takes
// raw base64 (no data: prefix) plus palette hexes; these helpers do the
// client-side prep. Pixel math is pure and unit-tested; DOM/File plumbing
// stays thin.

import {
  FACTORY_ASSET_PLATFORMS,
  FACTORY_ASSET_PLATFORM_TV,
  HOME_IMAGE_BRIGHT_CODE,
  HOME_IMAGE_BRIGHT_MEAN,
  TV_PLATFORM_KEYS,
  TV_PLATFORM_LABELS,
  TV_QUALITY_BLOCK_CODES,
  TV_QUALITY_BLOCK_FIXES,
  TV_QUALITY_GOOD_FILL_PCT,
  TV_QUALITY_ICON_SURFACES,
  TV_QUALITY_ROWS,
  TV_QUALITY_WARNING_COPY,
  TV_SYMBOL_MAX_ASPECT,
  TV_SYMBOL_MIN_PX,
  TV_TILE_TEXT_MAX,
  TV_BRAND_KIT_COPY,
  type FactoryAssetPlatform,
  type TvPlatformKey,
  type TvQualityStatus,
} from '@/lib/constants/appFactory';
import { APP_PLATFORM_INFO, APP_PLATFORM_KEYS } from '@/lib/constants/appPlatform';
import type { AppCfgsDto } from '@/lib/types/App';
import type {
  FactoryAssetQuality,
  FactoryAssetSet,
  FactoryBrandKit,
  FactoryBrandKitGenerateReq,
  FactoryBrandKitRemovable,
  FactoryBrandKitSeed,
  FactoryCurrentImage,
} from '@/lib/types/AppFactory';

export interface BrandKitPreset {
  key: string;
  label: string;
  bgColorHex: string;
  textColorHex: string;
}

/**
 * What a platform's LIVE images are: nothing yet, a set the Brand Kit (or the
 * earlier per-platform generator) made, or the customer's own art — uploaded,
 * or from before image sets were recorded.
 */
export type BrandKitLiveKind = 'none' | 'generated' | 'custom';

/** One platform's read, as the Brand Kit tab needs it. */
export interface BrandKitScopeData {
  assetSets: FactoryAssetSet[];
  currentImages: FactoryCurrentImage[];
}

/** One row of the quality table: a screen, its weakest file's numbers and its verdict. */
export interface BrandKitQualityRow {
  key: string;
  label: string;
  /** Percent of the screen's visible area the logo fills; undefined when the factory sent none. */
  fillPct?: number;
  /** The floor of that screen; undefined from an older factory. */
  minFillPct?: number;
  status: TvQualityStatus;
}

/** One cause a draft is blocked for, with the fix and the screens it shows on. */
export interface BrandKitBlockedFix {
  key: string;
  text: string;
  screens: string[];
}

/** The Brand Kit form's values (raw base64 already read for newly picked sources). */
export interface BrandKitFormInput {
  platforms: FactoryAssetPlatform[];
  symbolBase64?: string | null;
  wordmarkBase64?: string | null;
  tileArtBase64?: string | null;
  removeWordmark?: boolean;
  removeTileArt?: boolean;
  appName?: string | null;
  bgColorHex: string;
  textColorHex?: string | null;
  accentColorHex?: string | null;
}

export class BrandKitUtils {
  static readonly MAX_LOGO_BYTES = 8 * 1024 * 1024;

  static readonly PRESETS: BrandKitPreset[] = [
    { key: 'dark', label: 'Dark', bgColorHex: '#101820', textColorHex: '#ffffff' },
    { key: 'light', label: 'Light', bgColorHex: '#f5f5f2', textColorHex: '#1a1a1a' },
  ];

  /** Strips any data-URL prefix — the API wants raw base64. */
  static toRawBase64(dataUrl: string): string {
    const comma = dataUrl.indexOf(',');
    return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  }

  /**
   * Average color of the opaque pixels in RGBA data (the "from logo" accent
   * suggestion). Returns null when nothing is opaque enough to average.
   */
  static averageColorHex(rgba: Uint8ClampedArray): string | null {
    let r = 0;
    let g = 0;
    let b = 0;
    let n = 0;
    for (let i = 0; i + 3 < rgba.length; i += 4) {
      if (rgba[i + 3] < 128) continue; // transparent pixels say nothing about the brand
      r += rgba[i];
      g += rgba[i + 1];
      b += rgba[i + 2];
      n++;
    }
    if (n === 0) return null;
    return BrandKitUtils.rgbToHex(Math.round(r / n), Math.round(g / n), Math.round(b / n));
  }

  static rgbToHex(r: number, g: number, b: number): string {
    const c = (v: number) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0');
    return `#${c(r)}${c(g)}${c(b)}`;
  }

  /** Reads a File as raw base64 (data-URL prefix stripped). */
  static async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(BrandKitUtils.toRawBase64(String(reader.result)));
      reader.onerror = () => reject(new Error('Could not read the file.'));
      reader.readAsDataURL(file);
    });
  }

  /** Reads a File's pixel dimensions; null when undecodable. */
  static async readImageDimensions(file: File): Promise<{ w: number; h: number } | null> {
    const url = URL.createObjectURL(file);
    try {
      return await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = () => resolve(null);
        img.src = url;
      });
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  /**
   * PURE slot-pick verdict (vitest-covered): filename/size/dims against a
   * slot spec. dims null = undecodable. An 'exact' slot (the default) must
   * match w x h; a 'max' slot (a bounded mark) must fit within it — the
   * factory adds the trimmed-edges gate. Returns an error message or null.
   */
  static slotFileProblem(
    filename: string,
    sizeBytes: number,
    dims: { w: number; h: number } | null,
    slot: { label: string; w: number; h: number; format: 'png' | 'jpg'; sizeRule?: 'exact' | 'max'; altSizes?: { w: number; h: number }[] },
    maxBytes: number,
  ): string | null {
    const ext = filename.toLowerCase().split('.').pop() ?? '';
    const wantExts = slot.format === 'png' ? ['png'] : ['jpg', 'jpeg'];
    if (!wantExts.includes(ext)) {
      return `${slot.label} must be a ${slot.format.toUpperCase()} file.`;
    }
    if (sizeBytes > maxBytes) {
      return `${slot.label} must be under ${Math.round(maxBytes / 1024 / 1024)}MB.`;
    }
    if (!dims) {
      return `${slot.label} could not be decoded — use a valid ${slot.format.toUpperCase()}.`;
    }
    if (slot.sizeRule === 'max') {
      if (dims.w > slot.w || dims.h > slot.h) {
        return `${slot.label} must be at most ${slot.w} x ${slot.h} pixels (this image is ${dims.w} x ${dims.h}).`;
      }
      return null;
    }
    const sizes = [{ w: slot.w, h: slot.h }, ...(slot.altSizes ?? [])];
    if (!sizes.some((z) => dims.w === z.w && dims.h === z.h)) {
      const want = sizes.map((z) => `${z.w} x ${z.h}`).join(' or ');
      return `${slot.label} must be exactly ${want} pixels (this image is ${dims.w} x ${dims.h}).`;
    }
    return null;
  }

  /**
   * Browser wrapper: validates one manual-set slot pick (fast feedback;
   * the factory re-validates authoritatively).
   */
  static async validateSlotFile(
    file: File,
    slot: { label: string; w: number; h: number; format: 'png' | 'jpg'; sizeRule?: 'exact' | 'max'; altSizes?: { w: number; h: number }[] },
    maxBytes: number,
  ): Promise<string | null> {
    const ext = file.name.toLowerCase().split('.').pop() ?? '';
    const wantExts = slot.format === 'png' ? ['png'] : ['jpg', 'jpeg'];
    // Cheap checks first — skip the decode entirely on wrong type/size.
    if (!wantExts.includes(ext) || file.size > maxBytes) {
      return BrandKitUtils.slotFileProblem(file.name, file.size, null, slot, maxBytes);
    }
    const dims = await BrandKitUtils.readImageDimensions(file);
    return BrandKitUtils.slotFileProblem(file.name, file.size, dims, slot, maxBytes);
  }

  // ── TV graphics v2 (design C1): the Android-family Brand kit ──

  /**
   * The symbol's dominant BRAND colour: opaque pixels bucketed by 4-bit RGB,
   * near-neutral pixels (low saturation, or near black/white) skipped, the
   * fullest bucket's mean returned — the ground-colour suggestion. Null when
   * the mark is all neutral or transparent (the presets stay the choice).
   */
  static dominantBrandColorHex(rgba: Uint8ClampedArray): string | null {
    const buckets = new Map<number, { n: number; r: number; g: number; b: number }>();
    for (let i = 0; i + 3 < rgba.length; i += 4) {
      if (rgba[i + 3] < 128) continue;
      const r = rgba[i];
      const g = rgba[i + 1];
      const b = rgba[i + 2];
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      if (max < 40 || min > 225) continue; // near black / near white
      if (max === 0 || (max - min) / max < 0.25) continue; // greys
      const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
      const cur = buckets.get(key) ?? { n: 0, r: 0, g: 0, b: 0 };
      cur.n++;
      cur.r += r;
      cur.g += g;
      cur.b += b;
      buckets.set(key, cur);
    }
    let best: { n: number; r: number; g: number; b: number } | null = null;
    for (const v of buckets.values()) if (!best || v.n > best.n) best = v;
    if (!best) return null;
    return BrandKitUtils.rgbToHex(Math.round(best.r / best.n), Math.round(best.g / best.n), Math.round(best.b / best.n));
  }

  /**
   * PURE: the mean brightness of the opaque pixels, 0 (black) to 1 (white) —
   * the sRGB luma, the same number the factory judges the Home image by
   * (design ADR-0016 §Dashboard: a warning above 50 %). Null with nothing opaque.
   */
  static meanBrightness(rgba: Uint8ClampedArray): number | null {
    let sum = 0;
    let n = 0;
    for (let i = 0; i + 3 < rgba.length; i += 4) {
      if (rgba[i + 3] < 128) continue;
      sum += 0.2126 * rgba[i] + 0.7152 * rgba[i + 1] + 0.0722 * rgba[i + 2];
      n++;
    }
    return n === 0 ? null : sum / (255 * n);
  }

  /**
   * PURE advice on a picked background image (never a block): the factory's
   * own words when the picture is bright — the Home shows it as uploaded and
   * the text over it weakens. Null brightness (undecodable) = nothing to say.
   */
  static backgroundAdvice(brightness: number | null): string[] {
    return brightness != null && brightness > HOME_IMAGE_BRIGHT_MEAN ? [TV_BRAND_KIT_COPY.backgroundBright] : [];
  }

  /** True when any pixel is noticeably transparent (a PNG that can sit on the ground colour). */
  static hasTransparency(rgba: Uint8ClampedArray): boolean {
    for (let i = 3; i < rgba.length; i += 4) if (rgba[i] < 250) return true;
    return false;
  }

  /**
   * PURE advisory checks on a picked symbol (never a block — the factory
   * judges authoritatively): too wide for the circle, no transparency, too
   * small for a 4K TV. dims null = undecodable (the pick is refused elsewhere).
   */
  static symbolAdvice(dims: { w: number; h: number } | null, transparent: boolean | null): string[] {
    const out: string[] = [];
    if (!dims) return out;
    const aspect = Math.max(dims.w, dims.h) / Math.max(1, Math.min(dims.w, dims.h));
    if (aspect > TV_SYMBOL_MAX_ASPECT) out.push(TV_BRAND_KIT_COPY.wideSymbolWarning);
    if (transparent === false) out.push(TV_BRAND_KIT_COPY.noTransparency);
    if (Math.max(dims.w, dims.h) < TV_SYMBOL_MIN_PX) out.push(TV_BRAND_KIT_COPY.tooSmallSymbol);
    return out;
  }

  /**
   * PURE: the quality verdicts (C1) as customer messages, one per code with
   * the files it concerns, in first-seen order. An unknown code (a newer
   * factory) gets a neutral line naming it.
   */
  static qualityMessages(quality: FactoryAssetQuality[] | undefined | null): { code: string; text: string; files: string[]; known: boolean }[] {
    const byCode = new Map<string, { code: string; text: string; files: string[]; known: boolean }>();
    for (const q of quality ?? []) {
      for (const code of q.warnings ?? []) {
        const known = Object.prototype.hasOwnProperty.call(TV_QUALITY_WARNING_COPY, code);
        const entry = byCode.get(code) ?? {
          code,
          known,
          text: known ? TV_QUALITY_WARNING_COPY[code] : `Something to review on ${q.name}: ${code.replace(/_/g, ' ')}.`,
          files: [],
        };
        if (!entry.files.includes(q.name)) entry.files.push(q.name);
        byCode.set(code, entry);
      }
    }
    return [...byCode.values()];
  }

  /**
   * PURE (design D6): the "your TV tiles can look better" notice shows when
   * the live Android-family set predates composer v2 — the newest approved
   * set says composerVersion absent or 1, or there is no approved set but
   * the app has current images (a console-era set). Never while a draft is
   * pending (the customer is already regenerating). On Roku (design
   * roku_parity R6) only a factory-GENERATED v1 set is nudged: Roku apps
   * often carry designer-made posters (console-era or uploaded), and those
   * are the customer's own choice.
   */
  static needsTvTilesNotice(assetSets: FactoryAssetSet[], currentImages: FactoryCurrentImage[], platform: FactoryAssetPlatform = 'firetv'): boolean {
    if (assetSets.some((s) => s.state === 'generated')) return false;
    const approved = this.approvedByDecision(assetSets)[0];
    if (platform === 'roku') return !!approved && approved.source !== 'manual' && (approved.composerVersion ?? 1) < 2;
    if (approved) return (approved.composerVersion ?? 1) < 2;
    return currentImages.length > 0;
  }

  /**
   * PURE: approved sets, newest decision first. Approved is terminal, so a
   * row's `updated` is the moment it went live — not `created`: a draft made
   * earlier can be approved later and replace a newer set (3.36.1).
   */
  static approvedByDecision(assetSets: FactoryAssetSet[]): FactoryAssetSet[] {
    return assetSets
      .filter((s) => s.state === 'approved')
      .sort((a, b) => (a.updated < b.updated ? 1 : a.updated > b.updated ? -1 : 0));
  }

  /**
   * PURE: the decided sets for the history list, newest decision first, the
   * live set (the newest approval) flagged.
   */
  static historyRows(assetSets: FactoryAssetSet[]): { set: FactoryAssetSet; live: boolean }[] {
    const liveId = this.approvedByDecision(assetSets)[0]?.assetSetId;
    return assetSets
      .filter((s) => s.state !== 'generated')
      .sort((a, b) => (a.updated < b.updated ? 1 : a.updated > b.updated ? -1 : 0))
      .map((set) => ({ set, live: set.assetSetId === liveId }));
  }

  /**
   * PURE: the live set's approval when a pending draft was made BEFORE it —
   * approving that draft would replace newer art. Null otherwise.
   */
  static draftPredatesLive(pending: FactoryAssetSet | undefined, assetSets: FactoryAssetSet[]): FactoryAssetSet | null {
    const live = this.approvedByDecision(assetSets)[0];
    return pending && live && pending.created < live.updated ? live : null;
  }

  /**
   * PURE: Current Images, each URL pointed at the immutable copy inside the
   * approved set that last promoted that file. img/ reuses one name per file
   * and the static CDN keeps a name for up to a day (the objects carry no
   * Cache-Control and query strings are outside its cache key), so the img/
   * URL can keep showing art an approval just replaced; a set's own folder is
   * unique per set and never pruned while approved. Promotion is an overlay
   * (ADR-0017), so a name resolves to the newest approved set holding it; a
   * name no set holds (console-era art) keeps its img/ URL.
   */
  static liveImages(assetSets: FactoryAssetSet[], currentImages: FactoryCurrentImage[]): FactoryCurrentImage[] {
    const approved = this.approvedByDecision(assetSets);
    return currentImages.map((img) => {
      for (const s of approved) {
        const own = s.files?.find((f) => f.name === img.name);
        if (own) return { name: img.name, url: own.url };
      }
      return img;
    });
  }

  /**
   * PURE: the files a TV preview draws, name → URL. A set's own `files` win
   * (a draft previewed before approval); without them only the LIVE set can
   * be drawn, from Current Images. Null when nothing is drawable.
   */
  static previewFileMap(set: FactoryAssetSet | null, currentImages: FactoryCurrentImage[]): Record<string, string> | null {
    const list = set?.files && set.files.length > 0 ? set.files : set && set.state === 'generated' ? [] : currentImages;
    if (list.length === 0) return null;
    return Object.fromEntries(list.map((f) => [f.name, f.url]));
  }

  // ── App-level Brand Kit (3.42.0; design_brand_kit_app_level_20260930) ──

  /**
   * PURE: the TV platforms that are on for the app (a row exists), in the
   * fixed order. `allowed` is the account's entitlement per platform: a row
   * of a platform the account is not entitled to (a legacy Roku row) is off —
   * its whole surface is, so the Brand Kit never renders for it either.
   */
  static enabledTvPlatforms(
    app: Pick<AppCfgsDto, 'roku' | 'platforms'>,
    allowed: (platform: TvPlatformKey) => boolean = () => true,
  ): TvPlatformKey[] {
    const on = new Set<TvPlatformKey>();
    if (app.roku) on.add('roku');
    for (const key of APP_PLATFORM_KEYS) {
      const typeId = APP_PLATFORM_INFO[key].typeId;
      if ((app.platforms ?? []).some((r) => r.appPlatformTypeId === typeId)) on.add(key);
    }
    return TV_PLATFORM_KEYS.filter((k) => on.has(k) && allowed(k));
  }

  /**
   * PURE: the deliverable slots this app's customer manages — a slot only ONE
   * TV platform shows is left out while that platform is off (a Fire-TV-only
   * app never reads about the Google TV circle layers).
   */
  static slotsFor<T extends { tvOnly?: TvPlatformKey }>(slots: readonly T[], tv: readonly TvPlatformKey[]): T[] {
    return slots.filter((s) => !s.tvOnly || tv.includes(s.tvOnly));
  }

  /** PURE: the factory image sets those platforms are built from (Fire TV and Google TV share one). */
  static enabledScopes(tv: readonly TvPlatformKey[]): FactoryAssetPlatform[] {
    return FACTORY_ASSET_PLATFORMS.filter((scope) => FACTORY_ASSET_PLATFORM_TV[scope].some((k) => tv.includes(k)));
  }

  /** PURE: "A", "A and B", "A, B and C". */
  static joinLabels(labels: readonly string[]): string {
    if (labels.length <= 1) return labels[0] ?? '';
    return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
  }

  /** PURE: the platforms' names, joined ("Roku, Fire TV and Google TV"). */
  static tvLabel(tv: readonly TvPlatformKey[]): string {
    return this.joinLabels(tv.map((k) => TV_PLATFORM_LABELS[k]));
  }

  /**
   * PURE: one image set named by the platforms of it that are ON — a
   * Fire-TV-only app reads "Fire TV", never "Fire TV and Google TV".
   */
  static scopeLabel(scope: FactoryAssetPlatform, tv: readonly TvPlatformKey[]): string {
    const on = FACTORY_ASSET_PLATFORM_TV[scope].filter((k) => tv.includes(k));
    return this.tvLabel(on.length > 0 ? on : FACTORY_ASSET_PLATFORM_TV[scope]);
  }

  /**
   * PURE: what a platform's live images are. The newest approval decides: an
   * uploaded set is the customer's own art; a generated one is replaceable by
   * a new render. With no approval on record, current images are console-era
   * art — the customer's own.
   */
  static liveKind(assetSets: FactoryAssetSet[], currentImages: FactoryCurrentImage[]): BrandKitLiveKind {
    const approved = this.approvedByDecision(assetSets)[0];
    if (approved) return approved.source === 'manual' ? 'custom' : 'generated';
    return currentImages.length > 0 ? 'custom' : 'none';
  }

  /**
   * PURE (design B9): a platform with its own art is left out of a Generate
   * unless ticked — except while a Brand Kit draft of it is waiting: the
   * customer asked for that platform, and generating again must replace that
   * draft, not leave it behind from the older inputs.
   */
  static defaultTicks(
    scopes: readonly FactoryAssetPlatform[],
    kinds: Partial<Record<FactoryAssetPlatform, BrandKitLiveKind>>,
    draftScopes: readonly FactoryAssetPlatform[] = [],
  ): FactoryAssetPlatform[] {
    return scopes.filter((scope) => draftScopes.includes(scope) || (kinds[scope] ?? 'none') !== 'custom');
  }

  /**
   * PURE: the ticks the form shows — what the customer last chose (kept
   * across the form's remount when a Generate stores a new kit), limited to
   * the platforms that are still on; the defaults until they choose.
   */
  static effectiveTicks(
    chosen: readonly FactoryAssetPlatform[] | null,
    scopes: readonly FactoryAssetPlatform[],
    kinds: Partial<Record<FactoryAssetPlatform, BrandKitLiveKind>>,
    draftScopes: readonly FactoryAssetPlatform[] = [],
  ): FactoryAssetPlatform[] {
    return chosen ? scopes.filter((s) => chosen.includes(s)) : this.defaultTicks(scopes, kinds, draftScopes);
  }

  /** PURE: a colour the factory takes — #RGB or #RRGGBB. */
  static isHexColor(value: string | null | undefined): boolean {
    return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test((value ?? '').trim());
  }

  /**
   * PURE: the text colour a ground starts with — white on a dark ground, near
   * black on a light one (WCAG relative luminance; a seeded kit takes its
   * ground from the Roku poster, which may be light).
   */
  static textOnGround(bgColorHex: string): string {
    const light = BrandKitUtils.PRESETS.find((p) => p.key === 'light')!.textColorHex;
    const dark = BrandKitUtils.PRESETS.find((p) => p.key === 'dark')!.textColorHex;
    if (!this.isHexColor(bgColorHex)) return dark;
    let hex = bgColorHex.trim().slice(1);
    if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
    const lin = (i: number) => {
      const v = parseInt(hex.slice(i, i + 2), 16) / 255;
      return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };
    const luminance = 0.2126 * lin(0) + 0.7152 * lin(2) + 0.0722 * lin(4);
    return luminance > 0.4 ? light : dark;
  }

  /** PURE: a platform's pending draft (at most one is ever pending; the newest wins if an old factory left more). */
  static pendingDraft(assetSets: FactoryAssetSet[]): FactoryAssetSet | undefined {
    return assetSets
      .filter((s) => s.state === 'generated')
      .sort((a, b) => (a.created < b.created ? 1 : a.created > b.created ? -1 : 0))[0];
  }

  /**
   * PURE: the kit's quality report says its background image is bright
   * (`home_image_bright`, design ADR-0016): any set rendered from THIS kit
   * revision — a waiting draft or the approved set — whose quality carries the
   * code on any file. Keyed on the code, never on a filename, so the factory
   * may attach it to whichever file it judges.
   */
  static homeImageBright(
    kit: Pick<FactoryBrandKit, 'brandKitId'> | null,
    data: Partial<Record<FactoryAssetPlatform, Pick<BrandKitScopeData, 'assetSets'>>>,
  ): boolean {
    if (!kit) return false;
    return Object.values(data).some((d) =>
      (d?.assetSets ?? []).some(
        (set) => set.brandKitId === kit.brandKitId && (set.quality ?? []).some((q) => (q.warnings ?? []).includes(HOME_IMAGE_BRIGHT_CODE)),
      ),
    );
  }

  /**
   * PURE: the drafts the Brand Kit tab reviews — each platform's pending
   * draft that came from the kit, in platform order. A pending draft without
   * a kit (an upload, or a set from before the kit) is its platform tab's.
   */
  static kitDrafts(
    scopes: readonly FactoryAssetPlatform[],
    data: Partial<Record<FactoryAssetPlatform, BrandKitScopeData>>,
  ): { scope: FactoryAssetPlatform; set: FactoryAssetSet }[] {
    const out: { scope: FactoryAssetPlatform; set: FactoryAssetSet }[] = [];
    for (const scope of scopes) {
      const set = this.pendingDraft(data[scope]?.assetSets ?? []);
      if (set?.brandKitId) out.push({ scope, set });
    }
    return out;
  }

  /**
   * PURE (state walk 3): the platforms one click can fill from the stored
   * kit — on, with no live images and no draft waiting. Empty without a kit.
   */
  static quickScopes(
    kit: FactoryBrandKit | null | undefined,
    scopes: readonly FactoryAssetPlatform[],
    data: Partial<Record<FactoryAssetPlatform, BrandKitScopeData>>,
  ): FactoryAssetPlatform[] {
    if (!kit) return [];
    return scopes.filter((scope) => {
      const d = data[scope];
      if (!d) return false; // that platform's read is not in yet: never offer on a guess
      return this.liveKind(d.assetSets, d.currentImages) === 'none' && !this.pendingDraft(d.assetSets);
    });
  }

  /**
   * PURE: the kit decisions that cover these drafts — one per Brand Kit
   * revision (a `kit` decision reaches every pending draft of ONE revision),
   * each naming a draft of it and the platforms it covers. Normally one.
   */
  static kitGroups(
    drafts: readonly { scope: FactoryAssetPlatform; set: FactoryAssetSet }[],
  ): { brandKitId: string; assetSetId: string; scopes: FactoryAssetPlatform[] }[] {
    const groups: { brandKitId: string; assetSetId: string; scopes: FactoryAssetPlatform[] }[] = [];
    for (const d of drafts) {
      const id = d.set.brandKitId;
      if (!id) continue;
      const g = groups.find((x) => x.brandKitId === id);
      if (g) g.scopes.push(d.scope);
      else groups.push({ brandKitId: id, assetSetId: d.set.assetSetId, scopes: [d.scope] });
    }
    return groups;
  }

  /** PURE: a set whose gate is absent (uploads, older sets, an older factory) is not blocked. */
  static gatePassed(set: Pick<FactoryAssetSet, 'gate'>): boolean {
    return set.gate?.passed !== false;
  }

  /**
   * PURE: a Generate from the form (design C1, the full form). `bgColorHex`
   * marks it: the four scalars always ride (an empty string = none) and
   * replace the kit's; a source rides only when newly picked, and a stored
   * optional one the person removed is named in `remove` (a new pick wins
   * over a remove of the same slot).
   */
  static kitFormRequest(input: BrandKitFormInput): FactoryBrandKitGenerateReq {
    const req: FactoryBrandKitGenerateReq = {
      kit: true,
      platforms: [...input.platforms],
      bgColorHex: input.bgColorHex,
      textColorHex: input.textColorHex ?? '',
      accentColorHex: input.accentColorHex ?? '',
      tileText: (input.appName ?? '').trim().slice(0, TV_TILE_TEXT_MAX).trim(),
    };
    if (input.symbolBase64) req.symbolBase64 = input.symbolBase64;
    if (input.wordmarkBase64) req.wordmarkBase64 = input.wordmarkBase64;
    if (input.tileArtBase64) req.tileArtBase64 = input.tileArtBase64;
    const remove: FactoryBrandKitRemovable[] = [];
    if (input.removeWordmark && !input.wordmarkBase64) remove.push('wordmark');
    if (input.removeTileArt && !input.tileArtBase64) remove.push('tileArt');
    if (remove.length > 0) req.remove = remove;
    return req;
  }

  /** PURE: render the STORED kit for these platforms — nothing else may ride (design C1). */
  static kitStoredRequest(platforms: readonly FactoryAssetPlatform[]): FactoryBrandKitGenerateReq {
    return { kit: true, platforms: [...platforms] };
  }

  /**
   * PURE: whether the form has a mark to generate from — a newly picked one,
   * a stored one that is not being removed, or the seed's logo.
   */
  static hasMark(input: {
    symbolPicked: boolean;
    wordmarkPicked: boolean;
    removeWordmark: boolean;
    kit?: FactoryBrandKit | null;
    seed?: FactoryBrandKitSeed | null;
  }): boolean {
    if (input.symbolPicked || input.wordmarkPicked) return true;
    if (input.kit) return !!input.kit.symbolUrl || (!!input.kit.wordmarkUrl && !input.removeWordmark);
    if (input.seed) return input.seed.as === 'symbol' || !input.removeWordmark;
    return false;
  }

  /**
   * PURE: the app name the form starts with when the kit has none: the first
   * platform title that is set (Fire TV, Google TV, Roku), else the app's name.
   */
  static defaultAppName(app: Pick<AppCfgsDto, 'roku' | 'platforms' | 'appCfg'>): string {
    const titles: (string | undefined)[] = APP_PLATFORM_KEYS.map(
      (key) => (app.platforms ?? []).find((r) => r.appPlatformTypeId === APP_PLATFORM_INFO[key].typeId)?.title,
    );
    titles.push(app.roku?.core.title, app.appCfg.orgInfo.name);
    const first = titles.map((t) => (t ?? '').trim()).find((t) => t !== '') ?? '';
    return first.slice(0, TV_TILE_TEXT_MAX);
  }

  /** PURE: one file's verdict — a block wins (too small before hard to see), else good at 60 % and above. */
  static fillStatus(q: Pick<FactoryAssetQuality, 'fillPct' | 'blocked'>): TvQualityStatus {
    const blocked = q.blocked ?? [];
    if (blocked.includes(TV_QUALITY_BLOCK_CODES.SMALL_FILL)) return 'small';
    if (blocked.includes(TV_QUALITY_BLOCK_CODES.LOW_CONTRAST)) return 'hard';
    return (q.fillPct ?? 0) >= TV_QUALITY_GOOD_FILL_PCT ? 'good' : 'ok';
  }

  /**
   * PURE: a quality row's label for an app with these platforms on — the
   * first label whose platform is on; null = none of its platforms is on.
   */
  private static qualityLabel(name: string, tv: readonly TvPlatformKey[]): string | null {
    const info = TV_QUALITY_ROWS[name];
    if (!info) return name;
    const hit = info.labels.find((l) => l.platform === 'any' || tv.includes(l.platform));
    return hit ? hit.label : null;
  }

  /**
   * PURE: the quality table — one row per SCREEN, in the factory's order, the
   * files of one screen collapsed to the weakest (lowest fill, any block).
   * A screen of a platform the app does not have on is left out unless it
   * blocks, when it shows under a name that names no platform.
   */
  static qualityRows(quality: FactoryAssetQuality[] | undefined | null, tv: readonly TvPlatformKey[]): BrandKitQualityRow[] {
    const rank: Record<TvQualityStatus, number> = { good: 0, ok: 1, hard: 2, small: 3 };
    const rows = new Map<string, BrandKitQualityRow>();
    for (const q of quality ?? []) {
      // the home picture's line (surface `home`, appfactory ADR-0051) carries only its brightness
      // warning — it is not a screen the logo fills; nor is any warning-only entry
      if (q.surface === 'home' || (q.fillPct == null && !(q.blocked?.length))) continue;
      const status = this.fillStatus(q);
      const isBlocked = status === 'small' || status === 'hard';
      let label = this.qualityLabel(q.name, tv);
      if (label === null) {
        if (!isBlocked) continue;
        label = TV_QUALITY_ROWS[q.name].fallback;
      }
      const key = TV_QUALITY_ROWS[q.name]?.key ?? q.name;
      const cur = rows.get(key);
      if (!cur) {
        rows.set(key, { key, label, fillPct: q.fillPct, minFillPct: q.minFillPct, status });
        continue;
      }
      if (q.fillPct != null && (cur.fillPct == null || q.fillPct < cur.fillPct)) {
        cur.fillPct = q.fillPct;
        cur.minFillPct = q.minFillPct ?? cur.minFillPct;
      }
      if (rank[status] > rank[cur.status]) cur.status = status;
    }
    return [...rows.values()];
  }

  /**
   * PURE: why a draft is blocked, as fixes — one per cause, each with the
   * screens it shows on. A mark too small on an icon needs a square symbol;
   * on a wide surface it is a long name set in type (shorten it, or upload a
   * wordmark) or a lone wordmark (add a symbol so they stack); low contrast
   * needs another ground colour. An unknown code names itself.
   */
  static blockedFixes(set: Pick<FactoryAssetSet, 'gate' | 'quality'>, tv: readonly TvPlatformKey[]): BrandKitBlockedFix[] {
    const fixes = new Map<string, BrandKitBlockedFix>();
    const icons: readonly string[] = TV_QUALITY_ICON_SURFACES;
    for (const b of set.gate?.blocked ?? []) {
      const textFallback = (set.quality ?? []).find((q) => q.name === b.name)?.warnings?.includes('text_fallback') ?? false;
      let key: string;
      let text: string;
      if (b.code === TV_QUALITY_BLOCK_CODES.LOW_CONTRAST) {
        key = 'contrast';
        text = TV_QUALITY_BLOCK_FIXES.contrast;
      } else if (b.code === TV_QUALITY_BLOCK_CODES.SMALL_FILL) {
        key = icons.includes(b.surface) ? 'symbol' : textFallback ? 'longName' : 'stack';
        text = TV_QUALITY_BLOCK_FIXES[key as 'symbol' | 'longName' | 'stack'];
      } else {
        key = `code:${b.code}`;
        text = `${TV_QUALITY_BLOCK_FIXES.unknown}: ${b.code.replace(/_/g, ' ')}.`;
      }
      const screen = this.qualityLabel(b.name, tv) ?? TV_QUALITY_ROWS[b.name]?.fallback ?? b.name;
      const entry = fixes.get(key) ?? { key, text, screens: [] };
      if (!entry.screens.includes(screen)) entry.screens.push(screen);
      fixes.set(key, entry);
    }
    return [...fixes.values()];
  }

  /**
   * PURE: the advice lines of a draft that are NOT already said by a block
   * (a blocked small fill or low contrast is worded as its fix, once).
   */
  static adviceMessages(set: Pick<FactoryAssetSet, 'gate' | 'quality'>): { code: string; text: string; known: boolean }[] {
    const blockedCodes = new Set((set.gate?.blocked ?? []).map((b) => b.code));
    const blockedOnIcon = (set.gate?.blocked ?? []).some(
      (b) => b.code === TV_QUALITY_BLOCK_CODES.SMALL_FILL && (TV_QUALITY_ICON_SURFACES as readonly string[]).includes(b.surface),
    );
    return this.qualityMessages(set.quality).filter((m) => {
      if (blockedCodes.has(m.code)) return false;
      // the blocked icon's fix already says "add a square symbol"
      if (m.code === 'wide_logo_in_circle' && blockedOnIcon) return false;
      // a passing fill between the floor and "good" is shown as a number, not nagged about
      if (m.code === TV_QUALITY_BLOCK_CODES.SMALL_FILL) return false;
      return true;
    });
  }

  /**
   * PURE: the api gave up waiting for the factory (its hop answers 504 after
   * 15 s) while the factory was still rendering or publishing. The request is
   * not lost: the factory finishes it, and the page shows the result once it
   * re-reads.
   */
  static isStillWorking(err: unknown): boolean {
    return typeof err === 'object' && err !== null && (err as { httpStatusCode?: number }).httpStatusCode === 504;
  }

  /**
   * PURE: a refused generate or decision in the customer's words. The
   * factory's code, or the first field its validation names, picks the
   * sentence from `copy`; anything else keeps the error's own message.
   */
  static refusalMessage(
    err: { code?: string; problems?: { field: string; message: string }[]; message: string },
    copy: Record<string, string>,
  ): string {
    for (const p of err.problems ?? []) {
      const field = p.field.split('.')[0];
      // One field carries both "you sent none" and "the one you sent cannot be
      // read": only the first is answered with "choose one".
      const unreadable = copy[`${field}:unreadable`];
      if (unreadable && !/required/i.test(p.message)) return unreadable;
      if (copy[field]) return copy[field];
    }
    if (err.code && copy[err.code]) return copy[err.code];
    if (err.problems && err.problems.length > 0) return err.problems.map((p) => p.message).join(' ');
    return err.message;
  }

  /**
   * Browser wrapper: decodes a picked image into (downscaled) RGBA for the
   * colour suggestion and the transparency check. Null when undecodable or
   * when the canvas is unavailable (jsdom).
   */
  static async readImagePixels(file: File, maxSide = 256): Promise<{ w: number; h: number; rgba: Uint8ClampedArray } | null> {
    const url = URL.createObjectURL(file);
    try {
      const img = await new Promise<HTMLImageElement | null>((resolve) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => resolve(null);
        el.src = url;
      });
      if (!img) return null;
      const scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
      const cw = Math.max(1, Math.round(img.naturalWidth * scale));
      const ch = Math.max(1, Math.round(img.naturalHeight * scale));
      const canvas = document.createElement('canvas');
      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(img, 0, 0, cw, ch);
      return { w: img.naturalWidth, h: img.naturalHeight, rgba: ctx.getImageData(0, 0, cw, ch).data };
    } catch {
      return null;
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}
